import { AddressModel } from './address.model';
import { BookingStatusEnum } from './booking.model';
import {
  TrainingCategoryBackendListModel,
  TrainingCategoryBackendModel,
  TrainingCategoryModel,
} from './training-category.model';
import { UserBackendListModel, UserModel } from './user.model';

export enum SeminarStatus {
  CANCELED = 'canceled',
  WAITLIST = 'waitlist',
  FINISHED = 'finished',
  DRAFT = 'draft',
  RUNNING = 'running',
  ONLINE = 'online',
  PLANNED = 'planned',
  UNKNOWN = 'unknown',
}

export type SeminarDateModel = {
  id?: number;
  startDate: Date;
  endDate: Date;
  fobiPoints?: number;
  isCanceled?: boolean;
};

export type SeminarDateBackendModel = {
  id: number;
  start_date: string;
  end_date: string;
  fobi_points: number;
  is_canceled: boolean;
};

export type SeminarDateCreateUpdateModel = {
  id?: number;
  startDate: string;
  endDate: string;
  fobiPoints: number;
  isCanceled: boolean;
};

export type SeminarDateSeriesModel = {
  id?: number;
  name: string;
  participantCount?: number;
  currentUserStatus?: 'waitlist' | 'registered' | 'unregistered';
  dates: SeminarDateModel[];
};

export type SeminarDateSeriesBackendModel = {
  id: number;
  name: string;
  participant_count?: number;
  current_user_status?: 'waitlist' | 'registered' | 'unregistered';
  dates: SeminarDateBackendModel[];
};

export type SeminarDateSeriesCreateUpdateModel = {
  id?: number;
  name: string;
  dates: SeminarDateCreateUpdateModel[];
};

export type SeminarDateSeriesListModel = {
  dateSeriesId: number;
  dateSeriesName: string;
  seminar: {
    id: number;
    title: string;
    modality: string;
    link?: string;
    address?: AddressModel;
    maxParticipants: number;
    fobiPoints: boolean;
    isDraft: boolean;
    isCanceled: boolean;
  };
  lecturer1: UserModel;
  lecturer2?: UserModel;
  category1: TrainingCategoryBackendListModel;
  category2?: TrainingCategoryBackendListModel;
  dates: SeminarDateModel[];
  participantCount: number;
};

export type SeminarDateSeriesDetailsModel = {
  dateSeriesId: number;
  dateSeriesName: string;
  seminar: {
    id: number;
    title: string;
    modality: string;
    link?: string;
    platform?: string;
    meetingId?: string;
    meetingPassword?: string;
    address?: AddressModel;
    maxParticipants: number;
    recommendation: string;
    requirement: string;
    description: string;
    about: string;
    goals: string;
    benefit: string;
    seminarNr: string;
    conditions: SeminarConditionModel[];
    paypalPaymentAvailable: boolean;
    additionsTitle: string;
    additionsDescription: string;
    specialInterest: string;
    fobiPoints: boolean;
    isDraft: boolean;
    isCanceled: boolean;
    cancelReason?: string;
  };
  lecturer1: UserModel;
  lecturer2?: UserModel;
  category1: TrainingCategoryModel;
  category2?: TrainingCategoryModel;
  dates: SeminarDateModel[];
  participantCount: number;
  currentUserStatus?:
    | 'waitlist'
    | 'pending'
    | 'cancelled'
    | 'registered'
    | 'unregistered';
  sponsorCardIds?: number[];
  waitlistAttendeeCount?: number;
  waitlistBookingId?: number;
};

export type SeminarListModel = {
  id: number;
  title: string;
  category1: TrainingCategoryModel;
  category2?: TrainingCategoryModel;
  dateSeries: SeminarDateSeriesModel[];
  lecturer1: UserModel;
  lecturer2?: UserModel;
  fobiPoints: boolean;
  maxParticipants: number;
  modality: string;
  link?: string;
  address?: AddressModel;
  seminarNr: string;
  loaded: number;
  image?: string;
  isDraft: boolean;
  isCanceled: boolean;
};

export type SeminarModel = {
  id: number;
  title: string;
  description: string;
  category1: TrainingCategoryModel;
  category2?: TrainingCategoryModel;
  dateSeries: SeminarDateSeriesModel[];
  lecturer1: UserModel;
  lecturer2?: UserModel;
  fobiPoints: boolean;
  maxParticipants: number;
  recommendation?: string;
  requirement?: string;
  modality: string;
  link?: string;
  platform?: string | null;
  meetingId?: string | null;
  meetingPassword?: string | null;
  address?: AddressModel;
  about?: string;
  goals?: string;
  benefit?: string;
  seminarNr: string;
  conditions: SeminarConditionModel[];
  paypalPaymentAvailable: boolean;
  additionsTitle?: string;
  additionsDescription?: string;
  specialInterest?: string;
  loaded: number;
  image?: string;
  isDraft: boolean;
  isCanceled: boolean;
  cancelReason?: string;
  sponsorCardIds?: number[];
};

export type SeminarListBackendModel = {
  id: number;
  lecturer1: {
    id: number;
    first_name: string;
    last_name: string;
  };
  lecturer2?: {
    id: number;
    first_name: string;
    last_name: string;
  };
  category1: TrainingCategoryBackendListModel;
  category2?: TrainingCategoryBackendListModel;
  title: string;
  max_participants: number;
  modality: string;
  link?: string;
  address?: string;
  description: string;
  seminar_nr: string;
  fobi_points: number;
  date_series: SeminarDateSeriesBackendModel[];
  is_draft: boolean;
  is_canceled: boolean;
};

export type SeminarBackendModel = {
  id: number;
  lecturer1: {
    id: number;
    first_name: string;
    last_name: string;
  };
  lecturer2?: {
    id: number;
    first_name: string;
    last_name: string;
  };
  category1: TrainingCategoryBackendModel;
  category2?: TrainingCategoryBackendModel;
  title: string;
  max_participants: number;
  recommendation: string;
  requirement: string;
  modality: string;
  link?: string;
  platform?: string | null;
  meeting_id?: string | null;
  meeting_password?: string | null;
  address?: string;
  description: string;
  about: string;
  goals: string;
  benefit: string;
  seminar_nr: string;
  fobi_points: number;
  conditions: SeminarConditionModel[];
  paypal_payment_available: boolean;
  additions_title: string;
  additions_description: string;
  special_interest: string;
  date_series: SeminarDateSeriesBackendModel[];
  is_draft: boolean;
  is_canceled: boolean;
  cancel_reason?: string;
  sponsor_card_ids?: number[];
};

export type SeminarDateSeriesBackendListModel = {
  id_date_series: number;
  date_series_name: string;
  seminar: {
    id: number;
    title: string;
    modality: string;
    link?: string;
    address?: AddressModel;
    max_participants: number;
    fobi_points: number;
    is_draft: number;
    is_canceled: number;
  };
  lecturer1: UserBackendListModel;
  lecturer2?: UserBackendListModel;
  category1: TrainingCategoryBackendListModel;
  category2?: TrainingCategoryBackendListModel;
  dates: SeminarDateBackendModel[];
  participant_count: number;
};

export type SeminarDateSeriesDetailsBackendModel = {
  id_date_series: number;
  date_series_name: string;
  seminar: {
    id: number;
    title: string;
    modality: string;
    link?: string;
    platform?: string;
    meeting_id?: string;
    meeting_password?: string;
    address?: AddressModel;
    max_participants: number;
    recommendation: string;
    requirement: string;
    description: string;
    about: string;
    goals: string;
    benefit: string;
    seminar_nr: string;
    conditions: SeminarConditionModel[];
    paypal_payment_available: boolean;
    additions_title: string;
    additions_description: string;
    special_interest: string;
    fobi_points: number;
    is_draft: number;
    is_canceled: number;
    cancel_reason?: string;
  };
  lecturer1: UserBackendListModel;
  lecturer2?: UserBackendListModel;
  category1: TrainingCategoryBackendListModel;
  category2?: TrainingCategoryBackendListModel;
  dates: SeminarDateBackendModel[];
  participant_count: number;
  current_user_status?: 'waitlist' | 'registered' | 'unregistered';
  sponsor_card_ids?: number[];
  waitlist_attendee_count?: number;
  waitlist_booking_id?: number;
};

export type SeminarCreateUpdateModel = {
  id?: number;
  lecturerId: number;
  lecturer2Id?: number;
  categoryId: number;
  category2Id?: number;
  title: string;
  maxParticipants: number;
  recommendation: string;
  requirement?: string;
  modality: string;
  link?: string;
  platform?: string | null;
  meetingId?: string | null;
  meetingPassword?: string | null;
  address?: AddressModel;
  description: string;
  about: string;
  goals: string;
  benefit: string;
  seminarNr: string;
  fobiPoints: boolean;
  conditions: SeminarConditionModel[];
  paypalPaymentAvailable: boolean;
  additionsTitle: string;
  additionsDescription: string;
  specialInterest: string;
  isDraft: boolean;
  dateSeries: SeminarDateSeriesCreateUpdateModel[];
  fileIds?: number[];
  linkIds?: number[];
  sponsorCardIds?: number[];
};

export type SeminarAttendeeModel = {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  birthdate: Date;
  phoneNumber?: string;
  jobTitle?: string;
  condition?: string;
  conditionPrice?: number;
  status: BookingStatusEnum;
  createdAt: Date;
  bookingNr: string;
  bookingId: number;
  totalPrice?: number;
  discountedPrice?: number;
  couponCodes?: string;
  paymentMethod?: string;
};

export type SeminarAttendeeBackendModel = {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  birthdate: string;
  phone_number?: string;
  job_title?: string;
  condition?: string;
  condition_price?: number;
  status: BookingStatusEnum;
  created_at: string;
  booking_nr: string;
  booking_id: number;
  total_price?: number;
  discounted_total_price?: number;
  coupon_codes?: string;
  payment_method?: string;
};

export type CourseDateSeriesUpdateModel = {
  id?: number;
  name: string;
  dates: SeminarDateCreateUpdateModel[];
};

export type SeminarConditionModel = {
  id: number;
  type: SeminarConditionType;
  price: number;
  date?: string;
};

export enum SeminarConditionType {
  REGULAR = 'regular',
  EARLY = 'early',
  BED = 'bed',
  OFFICE = 'office',
  LOGO = 'logo',
  VDB = 'vdb',
  VDP = 'vdp',
}

export type SeminarFilterOptionModel = {
  id: number;
  title: string;
  dates: SeminarDateModel[];
  isDraft: boolean;
};

export type SeminarOnlyNameModel = {
  id: number;
  title: string;
};

export type SeminarDeleteInfoDetailModel = {
  dateSeriesName: string;
  registrationCount: number;
};

export type SeminarDeleteInfoModel = {
  pendingDateSeriesCount: number;
  totalRegistrationCount: number;
  details: SeminarDeleteInfoDetailModel[];
};

export type SeminarDeleteInfoBackendModel = {
  pending_date_series_count: number;
  total_registration_count: number;
  details: {
    date_series_name: string;
    registration_count: number;
  }[];
};
